import { useCallback } from "react";
import useSWR, { mutate } from "swr";
import useSWRImmutable from "swr/immutable";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  Configuration,
  InsertConfigurationBody,
  SearchAdminConfiguration,
  SearchConfiguration,
  UpdateConfigurationBody,
} from "./types/configurations";

export const useConfigurationsApi = () => {
  const api = useApi();

  const useProjectConfigurations = useCallback(
    (projectId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(`${isAdmin ? "/admin" : ""}/projects/${projectId}/configurations`, query);
      return useSWR<Paginated<SearchConfiguration>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchConfiguration>>({ url, signal }),
      );
    },
    [api],
  );

  const getConfiguration = useCallback(
    (configurationId: string, signal: AbortSignal) => {
      const url = `/configurations/${configurationId}`;
      return useSWRImmutable<Configuration, CustomError, string>(url, (url) => api.get<Configuration>({ url, signal }));
    },
    [api],
  );

  const useConfigurations = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/configurations`, query);
      return useSWR<Paginated<SearchConfiguration>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchConfiguration>>({ url, signal }),
      );
    },
    [api],
  );

  const useSharedConfigurations = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/configurations/shared`, query);
      return useSWR<Paginated<SearchConfiguration>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchConfiguration>>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminConfigurations = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/configurations`, query);
      return useSWR<Paginated<SearchAdminConfiguration>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchAdminConfiguration>>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminConfiguration = useCallback(
    (configurationId: string, signal: AbortSignal) => {
      const url = `/admin/configurations/${configurationId}`;
      return useSWRImmutable<Configuration, CustomError, string>(url, (url) => api.get<Configuration>({ url, signal }));
    },
    [api],
  );

  const insertConfiguration = useCallback(
    async (body: InsertConfigurationBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertConfigurationBody>(`/configurations`, body);
      mutateCache([
        "/projects",
        "/configurations",
        "/admin/projects",
        "/admin/configurations",
        ...(body.projectId ? [`/projects/${body.projectId}/configurations`] : []),
      ]);
      return id;
    },
    [api],
  );

  const updateConfiguration = useCallback(
    async (
      projectId: string | undefined,
      configurationId: string,
      body: UpdateConfigurationBody,
      optimisticData: Configuration,
    ): Promise<void> => {
      const url = `/configurations/${configurationId}`;
      mutate(url, optimisticData, { revalidate: false, populateCache: true }); // tested works
      await api.put<UpdateConfigurationBody>(url, body);
      mutateCache(
        [
          "/projects",
          "/configurations",
          "/admin/projects",
          "/admin/configurations",
          ...(projectId ? [`/projects/${projectId}/configurations`] : []),
          `/configurations/${configurationId}/items`,
        ],
        url,
      );
    },
    [api],
  );

  const deleteConfiguration = useCallback(
    async (projectId: string | undefined, configurationId: string): Promise<void> => {
      await api.del(`/configurations/${configurationId}`);
      mutateCache([
        "/projects",
        ...(projectId ? [`/projects/${projectId}/configurations`] : []),
        "/configurations",
        `/configurations/${configurationId}`,
        "/admin/projects",
        "/admin/configurations",
      ]);
    },
    [api],
  );

  const duplicateConfiguration = useCallback(
    async (
      projectId: string | undefined,
      configurationId: string,
      body: { projectId?: string; name: string },
      isAdmin?: boolean,
    ): Promise<void> => {
      await api.post(`${isAdmin ? "/admin" : ""}/configurations/${configurationId}/duplicate`, body);
      mutateCache([
        "/projects",
        ...(projectId ? [`/projects/${projectId}/configurations`] : []),
        "/configurations",
        `/configurations/${configurationId}`,
        "/admin/projects",
        ...(projectId ? [`/admin/projects/${projectId}/configurations`] : []),
        "/admin/configurations",
        `/admin/configurations/${configurationId}`,
      ]);
    },
    [api],
  );

  const addConfigurationToProject = useCallback(
    async (configurationId: string, projectId: string): Promise<Configuration> => {
      const url = `/configurations/${configurationId}`;
      const updatedConfiguration = await api.post<Configuration, { projectId: string }>(`${url}/project`, {
        projectId,
      });
      mutate(url, updatedConfiguration, { revalidate: false, populateCache: true });
      mutateCache([
        "/projects",
        "/configurations",
        "/admin/projects",
        "/admin/configurations",
        `/projects/${projectId}/configurations`,
        `${url}/items`,
      ]);
      return updatedConfiguration;
    },
    [api],
  );

  const removeConfigurationFromProject = useCallback(
    async (configurationId: string, projectId: string): Promise<Configuration> => {
      const url = `/configurations/${configurationId}`;
      const updatedConfiguration = await api.delWithResult<Configuration>(`${url}/project`);
      mutate(url, updatedConfiguration, { revalidate: false, populateCache: true });
      mutateCache([
        "/projects",
        "/configurations",
        "/admin/projects",
        "/admin/configurations",
        `/projects/${projectId}/configurations`,
        `${url}/items`,
      ]);
      return updatedConfiguration;
    },
    [api],
  );

  const removeSharedConfiguration = useCallback(
    async (configurationId: string): Promise<void> => {
      await api.del(`/configurations/${configurationId}/shared`);
      mutateCache(["/configurations/shared", `/configurations/${configurationId}`]);
    },
    [api],
  );

  return {
    useProjectConfigurations,
    getConfiguration,
    useAdminConfiguration,
    useConfigurations,
    useSharedConfigurations,
    useAdminConfigurations,
    insertConfiguration,
    updateConfiguration,
    deleteConfiguration,
    duplicateConfiguration,
    addConfigurationToProject,
    removeConfigurationFromProject,
    removeSharedConfiguration,
  };
};
